import { AnyType } from '@src/models/common';
import { isEqual } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LegoV2Context } from './context';
import { getSubscription } from './utils';

export const useLegoV2Context = () => {
  return useContext(LegoV2Context);
};

/**
 *  Хук для синхронизации значения с контроллером
 *  Основной хук для работы lego элементов
 *  id - уникальный идентификатор для lego элемента
 */
export const useLegoElementValue = (id: string) => {
  const { controller } = useLegoV2Context();

  const [value, setValue] = useState<AnyType>(controller.getValues()?.[id]);

  const result = useRef({});

  useEffect(() => {
    const subscription = getSubscription(controller.valueList$, id, setValue);

    return () => {
      subscription?.unsubscribe();
    };
  }, [controller.valueList$, id]);

  return Object.assign(result.current, { value, onChange: controller.onChange });
};

/**
 *  Хук для синхронизации значения с контроллером
 *  Основной хук для работы lego элементов
 *  id - уникальный идентификатор для lego элемента
 */
export const useLegoElementError = (id: string) => {
  const { controller } = useLegoV2Context();
  const [errorMessage, setErrorMessage] = useState<string[]>(controller.getErrors()?.[id]);

  const result = useRef({});

  useEffect(() => {
    const subscription = getSubscription(controller.errorList$, id, setErrorMessage);

    return () => {
      subscription?.unsubscribe();
    };
  }, [controller.errorList$, controller.valueList$, id]);

  return Object.assign(result.current, { errorMessage });
};

/**
 *  Хук для подписки на изменения внешних зависимостей
 *  `id` - это уникальный id lego элемента
 *  `externalDependenceList` - список ключей у объекта с внешними зависимостями на которые вы подписываетесь
 */
export const useExternalDependenceValues = (externalDependenceList: string[] = []) => {
  const { controller } = useLegoV2Context();
  const [value, setValue] = useState<{ [x: string]: AnyType }>(controller.getExternalDependencies());

  /** Это проверка значений массива на случай если будет меняться ссылка */
  const depArray = useRef(externalDependenceList);
  const dependenceList = useMemo(() => {
    if (!isEqual(depArray.current, externalDependenceList)) {
      depArray.current = externalDependenceList;
    }

    return depArray.current;
  }, [externalDependenceList]);

  useEffect(() => {
    const subscriptionList = dependenceList.map((depId) =>
      getSubscription(controller.externalDependenceList$, depId, (v) => setValue((prev) => ({ ...prev, [depId]: v }))),
    );

    return () => {
      subscriptionList.forEach((s) => s.unsubscribe());
    };
  }, [controller, dependenceList]);

  return value;
};
